import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const HomeContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 50px;
`;

const Title = styled.h1`
  font-size: 4rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  margin: 20px 0 0 0;
  opacity: 0.9;
  text-align: center;
  max-width: 600px;
`;

const NavigationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
  max-width: 1400px;
  width: 100%;
  margin-top: 50px;
`;

const NavigationCard = styled(Link)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 25px 20px;
  text-decoration: none;
  color: white;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
`;

const CardTitle = styled.h2`
  font-size: 2rem;
  margin: 0 0 20px 0;
  color: white;
`;

const CardDescription = styled.p`
  font-size: 1.1rem;
  margin: 0;
  opacity: 0.9;
  line-height: 1.6;
`;

const Icon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
  opacity: 0.8;
`;

function HomePage() {
  return (
    <HomeContainer>
      <Header>
        <Title>DSA Visualizer</Title>
        <Subtitle>
          Interactive visualization platform for learning Data Structures and Algorithms. 
          Explore sorting and searching algorithms with step-by-step animations.
        </Subtitle>
      </Header>
      
      <NavigationGrid>
        <NavigationCard to="/sorting">
          <Icon>📊</Icon>
          <CardTitle>Sorting Algorithms</CardTitle>
          <CardDescription>
            Visualize and learn various sorting algorithms including Bubble Sort, 
            Selection Sort, Insertion Sort, Merge Sort, and Quick Sort with 
            interactive animations and performance metrics.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/searching">
          <Icon>🔍</Icon>
          <CardTitle>Searching Algorithms</CardTitle>
          <CardDescription>
            Explore Linear Search and Binary Search algorithms with visual 
            representations, step-by-step execution, and complexity analysis.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/array-operations">
          <Icon>📋</Icon>
          <CardTitle>Array Operations</CardTitle>
          <CardDescription>
            Learn fundamental array operations including insertion, deletion, 
            searching, traversal, reversal, and sorting with interactive visualizations.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/linked-list">
          <Icon>🔗</Icon>
          <CardTitle>Linked List Operations</CardTitle>
          <CardDescription>
            Explore linked list data structure with operations like insertion, deletion, 
            searching, traversal, and reversal with step-by-step visualizations.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/graph">
          <Icon>🕸️</Icon>
          <CardTitle>Graph Algorithms</CardTitle>
          <CardDescription>
            Visualize graph algorithms including BFS, DFS, and Dijkstra's shortest path 
            with interactive node and edge animations.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/stack-queue">
          <Icon>📚</Icon>
          <CardTitle>Stack & Queue</CardTitle>
          <CardDescription>
            Explore stack (LIFO) and queue (FIFO) data structures with interactive 
            operations like push, pop, enqueue, dequeue, and peek with visual animations.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/tree">
          <Icon>🌳</Icon>
          <CardTitle>Tree Visualizer</CardTitle>
          <CardDescription>
            Visualize binary trees with interactive operations including insertion, 
            deletion, searching, and various traversal algorithms (BFS, DFS).
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/trie">
          <Icon>🔤</Icon>
          <CardTitle>Trie Visualizer</CardTitle>
          <CardDescription>
            Explore trie data structure with word insertion, deletion, searching, 
            and prefix matching operations. Visualize how strings are stored and retrieved efficiently.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/recursion-backtracking">
          <Icon>🔄</Icon>
          <CardTitle>Recursion & Backtracking</CardTitle>
          <CardDescription>
            Explore recursion and backtracking algorithms including factorial, fibonacci, 
            tower of hanoi, n-queens, maze solver, and sudoku solver with step-by-step visualizations.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/dynamic-programming">
          <Icon>⚡</Icon>
          <CardTitle>Dynamic Programming</CardTitle>
          <CardDescription>
            Visualize dynamic programming algorithms including Fibonacci, Longest Common Subsequence, 
            0/1 Knapsack, and Edit Distance with step-by-step table building animations.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/hashmap">
          <Icon>🗂️</Icon>
          <CardTitle>HashMap Visualizer</CardTitle>
          <CardDescription>
            Explore hash table operations including insertion, deletion, searching, and collision 
            handling with multiple hash function implementations and real-time statistics.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/bit-manipulation">
          <Icon>🔢</Icon>
          <CardTitle>Bit Manipulation</CardTitle>
          <CardDescription>
            Visualize bitwise operations including AND, OR, XOR, NOT, shifts, and advanced 
            techniques with 32-bit binary representation and step-by-step explanations.
          </CardDescription>
        </NavigationCard>
        
        <NavigationCard to="/heap">
          <Icon>🏗️</Icon>
          <CardTitle>Heap Visualizer</CardTitle>
          <CardDescription>
            Explore heap data structure operations including insertion, deletion, heapification, 
            and heap sort algorithm with tree and array representations.
          </CardDescription>
        </NavigationCard>
      </NavigationGrid>
    </HomeContainer>
  );
}

export default HomePage;
